/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  payoutService,
  type DisbursementLogRecord,
  type GetPayoutsParams,
  type PayoutLogRecord,
  type PayoutRecord,
} from "@src/@api-services/payout";
import {
  payoutScheduleService,
  type GetPayoutSchedulesParams,
  type PayoutScheduleRecord,
} from "@src/@api-services/payoutSchedule";
import { TableSort } from "@src/shared/constants/sort-order.constants";
import type { PaginatedListResponse } from "@src/shared/types/pagination.types";
import type {
  DisbursementLog,
  Payout,
  PayoutLog,
  PayoutScheduleConfig,
} from "@src/views/apps/financial/types";

interface PayoutsState {
  data: Payout[];
  total: number;
  params: GetPayoutsParams;
  selectedPayout: Payout | null;
  schedules: PayoutScheduleConfig[];
  schedulesTotal: number;
  logs: DisbursementLog[];
  loading: boolean;
  schedulesLoading: boolean;
  logsLoading: boolean;
  detailLoading: boolean;
  error: string | null;
}

const mapPayoutListItem = (row: PayoutRecord): Payout => ({
  id: String(row.id),
  businessId: String(row.businessId),
  businessName: row.businessName ?? null,
  totalAmount: Number(row.totalAmount ?? 0),
  platformFee: Number(row.platformFee ?? row.totalPlatformFee ?? 0),
  netAmount: Number(row.netAmount ?? 0),
  currency: String(row.currency ?? ""),
  status: row.status ?? null,
  scheduledAt: row.scheduledAt ?? null,
  processedAt: row.processedAt ?? null,
  createdAt: row.createdAt || new Date().toISOString(),
});

const mapPayoutLogItem = (log: PayoutLogRecord): PayoutLog => ({
  id: String(log.id),
  payoutId: String(log.payoutId),
  businessId: String(log.businessId),
  eventType: log.eventType ?? null,
  amount: Number(log.amount ?? 0),
  platformFee: Number(log.platformFee ?? 0),
  netAmount: Number(log.netAmount ?? 0),
  currency: String(log.currency ?? ""),
  status: log.status ?? null,
  message: log.message ?? null,
  createdAt: log.createdAt || new Date().toISOString(),
});

const mapPayoutDetail = (row: PayoutRecord): Payout => ({
  ...mapPayoutListItem(row),
  idempotencyKey: row.idempotencyKey ?? null,
  failureReason: row.failureReason ?? null,
  updatedAt: row.updatedAt ?? null,
  createdBy: row.createdBy ?? null,
  logs: Array.isArray(row.logs) ? row.logs.map(mapPayoutLogItem) : [],
});

const mapDisbursementLog = (row: DisbursementLogRecord): DisbursementLog => ({
  id: String(row.id),
  payoutId: String(row.payoutId),
  businessId: String(row.businessId),
  businessName: row.businessName ?? null,
  eventType: row.eventType ?? null,
  amount: Number(row.amount ?? 0),
  platformFee: Number(row.platformFee ?? 0),
  netAmount: Number(row.netAmount ?? 0),
  currency: String(row.currency ?? ""),
  status: row.status ?? null,
  message: row.message ?? null,
  createdAt: row.createdAt || new Date().toISOString(),
});

const mapPayoutSchedule = (row: PayoutScheduleRecord): PayoutScheduleConfig => ({
  id: String(row.id),
  businessId: String(row.businessId),
  businessName: row.businessName ?? null,
  frequency: row.frequency ?? "",
  isActive: Boolean(row.isActive),
  lastPayoutAt: row.lastPayoutAt ?? null,
  updatedAt: row.updatedAt || row.createdAt || new Date().toISOString(),
});

export const getPayouts = createAsyncThunk(
  "payouts/getPayouts",
  async (params: GetPayoutsParams, { rejectWithValue }) => {
    try {
      const response = await payoutService.getPayouts(params);
      const payload = response.data?.data as PaginatedListResponse<PayoutRecord>;
      const rows = payload?.data ?? [];
      return {
        params,
        data: rows.map(mapPayoutListItem),
        total: payload?.totalRecord ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payouts"
      );
    }
  }
);

export const getPayout = createAsyncThunk(
  "payouts/getPayout",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await payoutService.getPayout(id);
      const data = response.data?.data as PayoutRecord | undefined;
      return data?.id ? mapPayoutDetail(data) : null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payout"
      );
    }
  }
);

export const getPayoutSchedules = createAsyncThunk(
  "payouts/getPayoutSchedules",
  async (params: GetPayoutSchedulesParams, { rejectWithValue }) => {
    try {
      const response = await payoutScheduleService.getPayoutSchedules(params);
      const payload = (response.data?.data ?? response.data) as
        | PaginatedListResponse<PayoutScheduleRecord>
        | PayoutScheduleRecord[];
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      return {
        data: rows.map(mapPayoutSchedule),
        total: Number(
          !Array.isArray(payload) ? payload?.totalRecord ?? rows.length : rows.length
        ),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payout schedules"
      );
    }
  }
);

export const getDisbursementLogs = createAsyncThunk(
  "payouts/getDisbursementLogs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await payoutService.getDisbursementLogs();
      const payload = (response.data?.data ?? response.data) as
        | DisbursementLogRecord[]
        | undefined;
      const rows = Array.isArray(payload) ? payload : [];
      return rows.map(mapDisbursementLog);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch disbursement logs"
      );
    }
  }
);

const initialState: PayoutsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedPayout: null,
  schedules: [],
  schedulesTotal: 0,
  logs: [],
  loading: false,
  schedulesLoading: false,
  logsLoading: false,
  detailLoading: false,
  error: null,
};

export const payoutsSlice = createSlice({
  name: "payouts",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedPayout: (state) => {
      state.selectedPayout = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPayouts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPayouts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getPayouts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getPayout.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getPayout.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedPayout = action.payload;
      })
      .addCase(getPayout.rejected, (state) => {
        state.detailLoading = false;
        state.selectedPayout = null;
      })
      .addCase(getPayoutSchedules.pending, (state) => {
        state.schedulesLoading = true;
        state.error = null;
      })
      .addCase(getPayoutSchedules.fulfilled, (state, action) => {
        state.schedulesLoading = false;
        state.schedules = action.payload.data;
        state.schedulesTotal = action.payload.total;
      })
      .addCase(getPayoutSchedules.rejected, (state, action) => {
        state.schedulesLoading = false;
        state.error = action.payload as string;
        state.schedules = [];
        state.schedulesTotal = 0;
      })
      .addCase(getDisbursementLogs.pending, (state) => {
        state.logsLoading = true;
        state.error = null;
      })
      .addCase(getDisbursementLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.logs = action.payload;
      })
      .addCase(getDisbursementLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.payload as string;
        state.logs = [];
      });
  },
});

export const { clearError, clearSelectedPayout } = payoutsSlice.actions;
export default payoutsSlice.reducer;
